import { GeneralMuiModal } from '@components/shared/general-mui-modal';
import { Editor } from '@components/shared/HtmlEditor/HtmlEditor';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface SendOfferModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSendOffer: (offerData: SendOfferData) => Promise<void>;
	onCancelOffer?: () => Promise<void>;
	onChat?: () => void;
	currency?: string;
	initialPrice?: number;
	isEdit?: boolean;
	requestId?: string;
	farmerId?: string;
	specialistId?: string;
}

export interface SendOfferData {
	price: number;
	currency: string;
	message: string;
	requestId?: string;
	farmerId?: string;
	specialistId?: string;
}

export const SendOfferModal: React.FC<SendOfferModalProps> = ({
	isOpen,
	onClose,
	onSendOffer,
	onCancelOffer,
	onChat,
	currency = 'EUR',
	initialPrice = 25,
	isEdit = false,
	requestId,
	farmerId,
	specialistId,
}) => {
	const { t } = useTranslation();
	const [price, setPrice] = useState<number>(initialPrice);
	const [netAmount, setNetAmount] = useState<number>(0);
	const [serviceFee, setServiceFee] = useState<number>(0);
	const [message, setMessage] = useState<string>('');
	const [isSending, setIsSending] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	// Validate form fields
	useEffect(() => {
		// Message is now optional, so form is always valid
		// No validation for message field - empty comments are allowed
		setError(null);
	}, [message]);

	// Calculate derived values when price changes
	useEffect(() => {
		if (price) {
			const serviceFeeAmount = price * 0.18; // 18% service fee
			const netAmountValue = price - serviceFeeAmount;

			setServiceFee(parseFloat(serviceFeeAmount.toFixed(2)));
			setNetAmount(parseFloat(netAmountValue.toFixed(2)));
		}
	}, [price]);

	// Initialize values on open
	useEffect(() => {
		if (isOpen) {
			setPrice(initialPrice);
			const serviceFeeAmount = initialPrice * 0.18;
			const netAmountValue = initialPrice - serviceFeeAmount;
			setServiceFee(parseFloat(serviceFeeAmount.toFixed(2)));
			setNetAmount(parseFloat(netAmountValue.toFixed(2)));

			// Clear message and error state
			setMessage('');
			setError(null);
		}
	}, [isOpen, initialPrice]);

	const handleSendOffer = async () => {
		if (isSending) return;

		try {
			setIsSending(true);
			setError(null);

			// Allow empty message - no validation here
			await onSendOffer({
				price,
				currency,
				message: message || '', // Ensure message is never undefined
				requestId,
				farmerId,
				specialistId,
			});

			onClose();
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to send offer');
		} finally {
			setIsSending(false);
		}
	};

	const handleCancelOffer = async () => {
		if (isSending || !onCancelOffer) return;

		try {
			setIsSending(true);
			setError(null);

			await onCancelOffer();

			onClose();
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to cancel offer');
		} finally {
			setIsSending(false);
		}
	};

	return (
		<>
			<GeneralMuiModal
				open={isOpen}
				onClose={onClose}
				title={isEdit ? t('cancelOffer') : t('sendAnOffer')}
				styles={{
					paperStyle: { width: '500px', maxWidth: '100%' },
					actionsStyle: { justifyContent: 'space-between' },
				}}
				content={
					<Box className="flex flex-col gap-[18px]">
						{error && (
							<Alert severity="error" sx={{ mb: 2 }}>
								{error}
							</Alert>
						)}

						{/* Price and Currency Inputs */}
						<Box className="flex gap-[10px]">
							<Box sx={{ width: '50%' }}>
								<Typography variant="caption" sx={{ color: '#7B809A', mb: 1, display: 'block' }}>
									{t('price')}
								</Typography>
								<TextField
									fullWidth
									variant="outlined"
									size="small"
									type="number"
									value={price}
									disabled
									sx={{
										'& .MuiOutlinedInput-root': {
											borderRadius: '8px',
											backgroundColor: '#F1F3F5',
										},
									}}
								/>
							</Box>
							<Box sx={{ width: '50%' }}>
								<Typography variant="caption" sx={{ color: '#7B809A', mb: 1, display: 'block' }}>
									{t('unit')}
								</Typography>
								<TextField
									fullWidth
									variant="outlined"
									size="small"
									value={currency}
									disabled
									sx={{
										'& .MuiOutlinedInput-root': {
											borderRadius: '8px',
											backgroundColor: '#F1F3F5',
										},
									}}
								/>
							</Box>
						</Box>

						{/* Net Amount and Service Fee */}
						<Box className="flex gap-[10px]">
							<Box sx={{ width: '50%' }}>
								<Typography variant="caption" sx={{ color: '#7B809A', mb: 1, display: 'block' }}>
									{t('netAmount')}
								</Typography>
								<TextField
									fullWidth
									variant="outlined"
									size="small"
									type="number"
									value={netAmount}
									disabled
									sx={{
										'& .MuiOutlinedInput-root': {
											borderRadius: '8px',
											backgroundColor: '#F1F3F5',
										},
									}}
								/>
							</Box>
							<Box sx={{ width: '50%', position: 'relative' }}>
								<Typography variant="caption" sx={{ color: '#7B809A', mb: 1, display: 'block' }}>
									{t('serviceFee')}
									<Tooltip title={t('totalFee')} placement="top">
										<InfoOutlinedIcon
											fontSize="small"
											sx={{
												color: '#7B809A',
												fontSize: '16px',
												ml: '5px',
												verticalAlign: 'text-bottom',
											}}
										/>
									</Tooltip>
								</Typography>
								<TextField
									fullWidth
									variant="outlined"
									size="small"
									type="number"
									value={serviceFee}
									disabled
									sx={{
										'& .MuiOutlinedInput-root': {
											borderRadius: '8px',
											backgroundColor: '#F1F3F5',
										},
									}}
								/>
							</Box>
						</Box>

						{/* Comment Editor (not in edit mode) */}
						{!isEdit && (
							<Box sx={{ mt: 1, width: '100%' }}>
								<Typography variant="caption" sx={{ color: '#7B809A', mb: 1, display: 'block' }}>
									{t('comment')} ({t('optional')})
								</Typography>
								<Box sx={{ 
									width: '100%', 
									'& .quill': { 
										width: '100%',
										minHeight: '150px'
									},
									'& .ql-container': {
										minHeight: '100px'
									}
								}}>
									<Editor
										value={message}
										onChange={(value: string) => setMessage(value)}
										placeholder={t('addComment')}
										onBlur={() => {}}
									/>
								</Box>
								{/* Character count removed as requested */}
								{/* <Typography
									variant="caption"
									sx={{
										color: '#7B809A',
										mt: 1,
										display: 'block',
										textAlign: 'right',
									}}
								>
									{`${message.length}/1000`}
								</Typography> */}
							</Box>
						)}
					</Box>
				}
				actions={
					<>
						{isEdit ? (
							<div className="flex justify-end w-full">
								<Button
									variant="outlined"
									onClick={handleCancelOffer}
									disabled={isSending}
									sx={{
										padding: '10px 20px',
										fontWeight: '700',
										fontSize: '14px',
										backgroundColor: '#FD3333',
										color: 'white',
										border: 'unset',
										'&:hover': {
											backgroundColor: '#D32F2F',
										},
										'@media (max-width: 375px)': {
											padding: '10px',
										},
									}}
								>
									{isSending ? <CircularProgress size={24} color="inherit" /> : t('cancelOffer')}
								</Button>
							</div>
						) : (
							<>
								<Button
									variant="outlined"
									onClick={onChat}
									disabled={isSending}
									startIcon={<QuestionAnswerOutlinedIcon />}
									sx={{
										padding: '10px 20px',
										fontWeight: '700',
										fontSize: '14px',
										color: '#7B809A',
										border: 'unset',
										boxShadow: '0px 2px 6px 0px rgba(0, 0, 0, 0.25)',
										'&:hover': {
											backgroundColor: '#f5f5f5',
										},
										'@media (max-width: 375px)': {
											padding: '10px',
										},
									}}
								>
									<span className="text-dark">{t('chat')}</span>
								</Button>
								<Button
									variant="contained"
									onClick={handleSendOffer}
									endIcon={!isSending && <KeyboardArrowRightIcon />}
									disabled={isSending}
									sx={{
										padding: '10px 20px',
										fontWeight: '700',
										fontSize: '14px',
										backgroundColor: '#00AB89',
										'&:hover': {
											backgroundColor: '#008C6F',
										},
										'@media (max-width: 375px)': {
											padding: '10px',
										},
									}}
								>
									{isSending ? <CircularProgress size={24} color="inherit" /> : t('sendOffer')}
								</Button>
							</>
						)}
					</>
				}
			/>
		</>
	);
};
